import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { 
  MapPin, Star, Camera, Clock, Users, Car, 
  ArrowRight, Mountain, Utensils
} from "lucide-react";

const DestinationTemplate = ({ 
  cityName,
  stateName,
  heroTitle,
  heroSubtitle,
  metaDescription,
  keywords,
  attractions,
  experiences,
  bestTimeToVisit,
  howToReach,
  localCuisine,
  shopping,
  accommodation,
  travelTips,
  nearbyPlaces,
  seoContent,
  slug
}) => {
  const navigate = useNavigate();

  // Structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "TouristDestination",
    "name": `${cityName}, ${stateName}`,
    "description": metaDescription,
    "url": `${window.location.origin}/destinations/${slug}`,
    "image": `${window.location.origin}/logo512.png`,
    "address": {
      "@type": "PostalAddress",
      "addressLocality": cityName,
      "addressRegion": stateName,
      "addressCountry": "IN"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "23.1765",
      "longitude": "75.7885"
    },
    "touristType": [
      "Cultural Tourism",
      "Religious Tourism", 
      "Adventure Tourism",
      "Heritage Tourism"
    ],
    "amenityFeature": attractions.map(attraction => ({
      "@type": "LocationFeatureSpecification",
      "name": attraction.name,
      "value": attraction.description
    }))
  };

  return (
    <>
      <Helmet>
        <title>{`Best Places to Visit in ${cityName} ${stateName} | Aaoghumen Travel Guide`}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={keywords} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={`Best Places to Visit in ${cityName} ${stateName} | Aaoghumen`} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/destinations/${slug}`} />
        <meta property="og:image" content={`${window.location.origin}/logo512.png`} />
        <meta property="og:site_name" content="Aaoghumen" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Best Places to Visit in ${cityName} ${stateName}`} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={`${window.location.origin}/logo512.png`} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Aaoghumen Travel Team" />
        <link rel="canonical" href={`${window.location.origin}/destinations/${slug}`} />
        
        {/* Location Schema */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-green-600 via-blue-600 to-purple-700 text-white overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="destination-pattern" width="20" height="20" patternUnits="userSpaceOnUse">
                  <circle cx="10" cy="10" r="2" fill="currentColor"/>
                  <path d="M 20 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#destination-pattern)" />
            </svg>
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4 py-20 sm:py-28">
            <div className="text-center">
              <div className="mb-6">
                <span className="inline-block bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold">
                  🏛️ Discover {stateName}
                </span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 tracking-tight leading-tight">
                <span className="text-yellow-300">{heroTitle}</span>
                <span className="block text-white">{cityName}</span>
              </h1>
              
              <p className="text-xl sm:text-2xl opacity-90 max-w-4xl mx-auto leading-relaxed mb-8">
                {heroSubtitle}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate("/onboarding")}
                  className="bg-white text-blue-600 px-8 py-4 rounded-2xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-lg"
                >
                  🗺️ Plan Your Trip
                </button>
                <button
                  onClick={() => navigate("/gallery")}
                  className="border-2 border-white text-white px-8 py-4 rounded-2xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300 text-lg"
                >
                  📸 View Gallery
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Info Bar */}
        <div className="bg-white shadow-lg -mt-8 relative z-10 mx-4 rounded-2xl">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
              <div className="flex items-center justify-center space-x-3">
                <MapPin className="w-6 h-6 text-blue-600" />
                <div>
                  <div className="font-semibold text-gray-800">{cityName}</div>
                  <div className="text-sm text-gray-600">{stateName}, India</div>
                </div>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <Clock className="w-6 h-6 text-green-600" />
                <div>
                  <div className="font-semibold text-gray-800">Best Time</div>
                  <div className="text-sm text-gray-600">{bestTimeToVisit}</div>
                </div>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <Star className="w-6 h-6 text-yellow-500" />
                <div>
                  <div className="font-semibold text-gray-800">Top Rated</div>
                  <div className="text-sm text-gray-600">4.8/5 Rating</div>
                </div>
              </div>
              <div className="flex items-center justify-center space-x-3">
                <Users className="w-6 h-6 text-purple-600" />
                <div>
                  <div className="font-semibold text-gray-800">Guide Available</div>
                  <div className="text-sm text-gray-600">Local Experts</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 py-16">
          
          {/* Top Attractions */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
                Top Attractions in {cityName}
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Discover the most beautiful and culturally significant places that make {cityName} special
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {attractions.map((attraction, index) => (
                <div 
                  key={index} 
                  className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
                  onClick={() => attraction.path && attraction.path !== "#" && navigate(attraction.path)}
                >
                  <div className="h-48 overflow-hidden">
                    {attraction.image ? (
                      <img 
                        src={attraction.image} 
                        alt={attraction.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextElementSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div className="w-full h-full bg-gradient-to-br from-blue-200 to-green-200 flex items-center justify-center" style={{display: attraction.image ? 'none' : 'flex'}}>
                      <Camera className="w-16 h-16 text-blue-500" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors">
                      {attraction.name}
                    </h3>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      {attraction.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <Star className="w-4 h-4 text-yellow-500" />
                        <span>{attraction.rating}/5</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-500">{attraction.category}</span>
                        {attraction.path && attraction.path !== "#" && (
                          <ArrowRight className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Experiences */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
                Unique Experiences
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Immerse yourself in authentic local experiences and create unforgettable memories
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {experiences.map((experience, index) => (
                <div key={index} className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 hover:shadow-lg transition-all duration-300">
                  <div className="flex items-start space-x-4">
                    <div className="bg-gradient-to-br from-purple-500 to-blue-500 text-white p-3 rounded-xl">
                      {experience.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{experience.title}</h3>
                      <p className="text-gray-600 leading-relaxed mb-4">{experience.description}</p>
                      <div className="text-sm text-purple-600 font-semibold">
                        Duration: {experience.duration}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Travel Information Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            
            {/* How to Reach */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <Car className="w-8 h-8 text-blue-600" />
                <h3 className="text-2xl font-bold text-gray-800">How to Reach {cityName}</h3>
              </div>
              <div className="space-y-4">
                {howToReach.map((option, index) => (
                  <div key={index} className="border-l-4 border-blue-200 pl-4">
                    <h4 className="font-semibold text-gray-800 mb-1">{option.mode}</h4>
                    <p className="text-gray-600 text-sm">{option.details}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Local Cuisine */}
            <div className="bg-white rounded-3xl p-8 shadow-lg">
              <div className="flex items-center space-x-3 mb-6">
                <Utensils className="w-8 h-8 text-orange-600" />
                <h3 className="text-2xl font-bold text-gray-800">Local Cuisine</h3>
              </div>
              <div className="space-y-4">
                {localCuisine.map((dish, index) => (
                  <div key={index} className="border-l-4 border-orange-200 pl-4">
                    <h4 className="font-semibold text-gray-800 mb-1">{dish.name}</h4>
                    <p className="text-gray-600 text-sm">{dish.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Travel Tips */}
          <div className="mb-20">
            <div className="bg-gradient-to-br from-green-600 to-blue-600 rounded-3xl p-12 text-white">
              <h2 className="text-3xl font-bold mb-8 text-center">
                Essential Travel Tips for {cityName}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {travelTips.map((tip, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="bg-white/20 p-3 rounded-xl">
                      <ArrowRight className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">{tip.title}</h4>
                      <p className="text-green-100">{tip.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Nearby Places */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-4">
                Nearby Destinations
              </h2>
              <p className="text-gray-600 text-lg max-w-3xl mx-auto">
                Extend your journey and explore these amazing places near {cityName}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {nearbyPlaces.map((place, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer" 
                     onClick={() => navigate(`/destinations/${place.slug}`)}>
                  <div className="h-32 bg-gradient-to-br from-purple-200 to-pink-200 rounded-xl flex items-center justify-center mb-4">
                    <Mountain className="w-12 h-12 text-purple-500" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{place.name}</h3>
                  <p className="text-gray-600 text-sm mb-3">{place.distance}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-600 font-semibold text-sm">Explore →</span>
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span className="text-sm text-gray-600">{place.rating}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 rounded-3xl p-12 text-white text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Ready to Explore {cityName}?
            </h2>
            <p className="text-xl opacity-90 max-w-3xl mx-auto leading-relaxed mb-8">
              Start your journey with Aaoghumen and discover the magic of {cityName}. Our local experts are ready to show you the best of {stateName}.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate("/onboarding")}
                className="bg-white text-blue-600 px-8 py-4 rounded-2xl font-semibold hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 text-lg"
              >
                🎯 Plan Your Trip Now
              </button>
              <button
                onClick={() => navigate("/blog")}
                className="border-2 border-white text-white px-8 py-4 rounded-2xl font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300 text-lg"
              >
                📖 Read More Stories
              </button>
            </div>
          </div>
        </div>

        {/* SEO Content Footer */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                {cityName} Travel Guide by Aaoghumen
              </h3>
              <div className="prose prose-lg max-w-4xl mx-auto text-gray-600 leading-relaxed">
                <p>{seoContent}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mt-12">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">{attractions.length}+</div>
                  <div className="text-gray-600">Top Attractions</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">24/7</div>
                  <div className="text-gray-600">Travel Support</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">100+</div>
                  <div className="text-gray-600">Local Guides</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">4.8★</div>
                  <div className="text-gray-600">Average Rating</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DestinationTemplate;